import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { User, LoginCredentials, RegisterCredentials } from '../types';
import { getUtmParams, clearUtm } from '../utils/utm';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasCompletedOnboarding: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  register: (credentials: RegisterCredentials) => Promise<{ etsy_invite_id?: number | null }>;
  loginWithToken: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() =>
    typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null
  );
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  // Set auth header when token changes
  useEffect(() => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete apiClient.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Fetch user on mount if token exists
  useEffect(() => {
    async function initAuth() {
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await apiClient.get('/api/v1/auth/user');
        setUser(response.data);
      } catch {
        setToken(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const response = await apiClient.post('/api/v1/login', credentials);
    const authToken = response.data.token;

    // Immediately set the auth header BEFORE updating state
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    localStorage.setItem(TOKEN_KEY, authToken);

    setToken(authToken);
    setUser(response.data.user);
  };

  const register = async (credentials: RegisterCredentials): Promise<{ etsy_invite_id?: number | null }> => {
    const utmParams = getUtmParams();
    const response = await apiClient.post('/api/v1/register', {
      ...utmParams,
      ...credentials,
    });
    const authToken = response.data.token;

    // Immediately set the auth header BEFORE updating state
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
    localStorage.setItem(TOKEN_KEY, authToken);

    clearUtm();
    setToken(authToken);
    setUser(response.data.user);

    // Meta Pixel + GA4 — fire on successful signup
    import('../utils/analytics').then(({ trackMeta, track }) => {
      trackMeta('Lead');
      track('sign_up');
    });

    return { etsy_invite_id: response.data.etsy_invite_id ?? null };
  };

  const logout = async () => {
    try {
      await apiClient.post('/api/v1/auth/logout');
    } finally {
      // Clear immediately to prevent race conditions
      localStorage.removeItem(TOKEN_KEY);
      delete apiClient.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
      queryClient.clear();
    }
  };

  const refreshUser = async () => {
    const response = await apiClient.get('/api/v1/auth/user');
    setUser(response.data);
  };

  const loginWithToken = async (newToken: string) => {
    // Set the token in state (which triggers the useEffect to set localStorage and header)
    setToken(newToken);
    // Also set the header immediately for the next request
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    // Fetch user data
    const response = await apiClient.get('/api/v1/auth/user');
    setUser(response.data);
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    hasCompletedOnboarding: user?.onboarding_completed ?? false,
    login,
    register,
    loginWithToken,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
